import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../hooks/useCredits';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  Stack,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Avatar,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Card,
  CardContent,
  CardActions,
  Slider,
  InputLabel,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ButtonGroup,
  Chip,
  RadioGroup,
  Radio,
  FormLabel
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  Download as DownloadIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Tune as TuneIcon,
  ColorLens as ColorLensIcon,
  TextFields as TextFieldsIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  LinkedIn as LinkedInIcon,
  AutoAwesome as AutoAwesomeIcon,
  Person as PersonIcon,
  PostAdd as PostAddIcon,
  ExpandMore as ExpandMoreIcon,
  Upload as UploadIcon,
  PhotoLibrary as PhotoLibraryIcon,
  FormatSize as FormatSizeIcon,
  Palette as PaletteIcon,
  CropFree as CropFreeIcon,
  Opacity as OpacityIcon,
  RotateRight as RotateRightIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { toPng } from 'html-to-image';
import { keyframes } from '@mui/system';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import axios from 'axios';
import LoadingScreen from '../components/common/LoadingScreen';
import { Helmet } from 'react-helmet-async';
import { toast, Toaster } from 'react-hot-toast'; // Added import

// Define the fade animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Add horizontal slide animations
const slideLeft = keyframes`
  from {
    opacity: 0.5;
    transform: translateX(100px) rotate(1deg);
  }
  to {
    opacity: 1;
    transform: translateX(0) rotate(0deg);
  }
`;

const slideRight = keyframes`
  from {
    opacity: 0.5;
    transform: translateX(-100px) rotate(-1deg);
  }
  to {
    opacity: 1;
    transform: translateX(0) rotate(0deg);
  }
`;

const GenerateCarousel = ({ 
  isModalMode = false, 
  onCarouselGenerated, 
  initialContent = '', 
  onClose 
}) => {
  const { user, setUser } = useAuth();
  const { refetchCredits } = useCredits();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  
  // State management
  const [componentMounted, setComponentMounted] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [totalSlides, setTotalSlides] = useState(6); // Default to 6 slides
  const [slides, setSlides] = useState([]);
  const [slideSize, setSlideSize] = useState('portrait');
  const [forceKey, setForceKey] = useState(Date.now()); // Force re-render with a key
  const [activeTab, setActiveTab] = useState('generate'); // Changed default to 'generate'
  const [theme, setTheme] = useState('ocean-breeze');
  const [fontPairing, setFontPairing] = useState('montserrat-roboto');
  const [topic, setTopic] = useState('');
  const [inputText, setInputText] = useState('');
  const [inputMode, setInputMode] = useState('topic'); // 'topic' or 'text'
  const [generating, setGenerating] = useState(false);
  const [visualElement, setVisualElement] = useState('none');
  const [borderStyle, setBorderStyle] = useState('none');
  const [displayName, setDisplayName] = useState(user?.name || '');
  const [profileImage, setProfileImage] = useState(user?.image || '');
  const [titleFont, setTitleFont] = useState('montserrat');
  const [contentFont, setContentFont] = useState('roboto');
  const [backgroundStyle, setBackgroundStyle] = useState('solid');
  const [linkedinProfileData, setLinkedinProfileData] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [postsDialogOpen, setPostsDialogOpen] = useState(false);
  const [availablePosts, setAvailablePosts] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Enhanced customization states
  const [titleFontSize, setTitleFontSize] = useState(40); // px
  const [contentFontSize, setContentFontSize] = useState(16); // px
  const [titleFontWeight, setTitleFontWeight] = useState(700);
  const [contentFontWeight, setContentFontWeight] = useState(400);
  const [lineHeight, setLineHeight] = useState(1.4);
  const [letterSpacing, setLetterSpacing] = useState(0); // px
  const [textAlign, setTextAlign] = useState('left');
  const [padding, setPadding] = useState(32); // px
  const [cornerRadius, setCornerRadius] = useState(8); // px
  
  // Image states
  const [slideImages, setSlideImages] = useState({}); // Store images per slide
  const [currentSlideImage, setCurrentSlideImage] = useState(null);
  const [imagePosition, setImagePosition] = useState('top-right'); // top-left, top-right, bottom-left, bottom-right, center, background
  const [imageSize, setImageSize] = useState(100); // px
  const [imageOpacity, setImageOpacity] = useState(1);
  const [imageRotation, setImageRotation] = useState(0); // degrees
  
  // Advanced styling states
  const [shadowIntensity, setShadowIntensity] = useState(10);
  const [gradientDirection, setGradientDirection] = useState(135); // degrees
  const [borderWidth, setBorderWidth] = useState(3); // px
  const [customColor1, setCustomColor1] = useState('#4890fe');
  const [customColor2, setCustomColor2] = useState('#ffffff');
  const [useCustomColors, setUseCustomColors] = useState(false);
  
  // UI references
  const previewRef = useRef(null);
  const editorRef = useRef(null);
  const titleEditorRef = useRef(null);
  const resizeObserver = useRef(null);
  const resizeTimeout = useRef(null);
  
  // Quill editor configurations
  const quillModules = {
    toolbar: [
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
  };

  const titleQuillModules = {
    toolbar: [
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline'],
      [{ 'color': [] }],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const quillFormats = [
    'size',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link'
  ];

  const titleQuillFormats = [
    'size',
    'bold', 'italic', 'underline',
    'color',
    'align'
  ];

  // Define example slides directly in the component
  const exampleSlides = [
    {
      title: "<p>Why Use Carousels?</p>",
      content: "<p>Carousels capture attention with movement and allow you to share multiple ideas in limited space.</p><p>Perfect for social media and presentations!</p>"
    },
    {
      title: "<p>Boost Engagement</p>",
      content: "<p>→ Increases view time by 58%</p><p>→ Drives 72% more interaction</p><p>→ Makes content more shareable</p>"
    },
    {
      title: "<p>Create With Instation</p>",
      content: "<p>Generate professional carousels in minutes, not hours.</p><p>Just enter a topic and let AI do the work!</p>"
    }
  ];

  // Slide themes/templates
  const slideThemes = [
    { value: 'royal-twilight', label: 'Royal Twilight', primary: '#39286E', secondary: '#E99C2E' },
    { value: 'ocean-breeze', label: 'Ocean Breeze', primary: '#1976d2', secondary: '#ffffff' },
    { value: 'forest-green', label: 'Forest Green', primary: '#2e7d32', secondary: '#ffffff' },
    { value: 'rose-gold', label: 'Rose Gold', primary: '#c2185b', secondary: '#f8bbd0' },
    { value: 'dark-mode', label: 'Dark Mode', primary: '#212121', secondary: '#ffffff' },
    { value: 'sunset-orange', label: 'Sunset Orange', primary: '#FF5722', secondary: '#FFF3E0' },
    { value: 'deep-purple', label: 'Deep Purple', primary: '#673AB7', secondary: '#ffffff' },
    { value: 'teal-mint', label: 'Teal Mint', primary: '#009688', secondary: '#E0F2F1' },
    { value: 'crimson-gold', label: 'Crimson Gold', primary: '#B71C1C', secondary: '#FFF176' },
    { value: 'midnight-blue', label: 'Midnight Blue', primary: '#0D47A1', secondary: '#E3F2FD' },
    { value: 'amber-brown', label: 'Amber Brown', primary: '#795548', secondary: '#FFECB3' },
    { value: 'slate-gray', label: 'Slate Gray', primary: '#455A64', secondary: '#ECEFF1' }
  ];

  // Define font options
  const fontOptions = [
    { value: 'montserrat', label: 'Montserrat', family: "'Montserrat', sans-serif" },
    { value: 'roboto', label: 'Roboto', family: "'Roboto', sans-serif" },
    { value: 'playfair', label: 'Playfair Display', family: "'Playfair Display', serif" },
    { value: 'source-sans', label: 'Source Sans Pro', family: "'Source Sans Pro', sans-serif" },
    { value: 'poppins', label: 'Poppins', family: "'Poppins', sans-serif" },
    { value: 'open-sans', label: 'Open Sans', family: "'Open Sans', sans-serif" },
    { value: 'oswald', label: 'Oswald', family: "'Oswald', sans-serif" },
    { value: 'nunito', label: 'Nunito', family: "'Nunito', sans-serif" },
    { value: 'lora', label: 'Lora', family: "'Lora', serif" },
    { value: 'raleway', label: 'Raleway', family: "'Raleway', sans-serif" },
    { value: 'merriweather', label: 'Merriweather', family: "'Merriweather', serif" },
    { value: 'lato', label: 'Lato', family: "'Lato', sans-serif" },
    { value: 'quicksand', label: 'Quicksand', family: "'Quicksand', sans-serif" },
    { value: 'georgia', label: 'Georgia', family: "Georgia, serif" },
    { value: 'helvetica', label: 'Helvetica', family: "Helvetica, sans-serif" }
  ];

  // Define font pairings (keep this for backward compatibility)
  const fontPairings = [
    { value: 'montserrat-roboto', label: 'Montserrat / Roboto', heading: "'Montserrat', sans-serif", body: "'Roboto', sans-serif" },
    { value: 'playfair-source-sans', label: 'Playfair / Source Sans', heading: "'Playfair Display', serif", body: "'Source Sans Pro', sans-serif" },
    { value: 'poppins-open-sans', label: 'Poppins / Open Sans', heading: "'Poppins', sans-serif", body: "'Open Sans', sans-serif" },
    { value: 'oswald-nunito', label: 'Oswald / Nunito', heading: "'Oswald', sans-serif", body: "'Nunito', sans-serif" }
  ];

  // Define background style options
  const backgroundStyles = [
    { 
      value: 'solid', 
      label: 'Solid Color',
      preview: (color) => ({
        backgroundColor: color
      })
    },
    { 
      value: 'gradient-linear', 
      label: 'Linear Gradient',
      preview: (color) => ({
        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
        backgroundSize: '100% 100%'
      })
    },
    { 
      value: 'gradient-radial', 
      label: 'Radial Gradient',
      preview: (color) => ({
        background: `radial-gradient(circle, ${color} 0%, ${color}dd 100%)`,
        backgroundSize: '100% 100%'
      })
    },
    { 
      value: 'gradient-diagonal', 
      label: 'Diagonal Gradient',
      preview: (color) => ({
        background: `linear-gradient(45deg, ${color} 0%, ${color}aa 50%, ${color}dd 100%)`,
        backgroundSize: '100% 100%'
      })
    },
    { 
      value: 'grid', 
      label: 'Grid Pattern',
      preview: (color, secondary) => ({
        backgroundColor: color,
        backgroundImage: `linear-gradient(${secondary}22 1px, transparent 1px), linear-gradient(90deg, ${secondary}22 1px, transparent 1px)`,
        backgroundSize: '10px 10px'
      })
    },
    { 
      value: 'dots', 
      label: 'Polka Dots',
      preview: (color, secondary) => ({
        backgroundColor: color,
        backgroundImage: `radial-gradient(${secondary}33 1px, transparent 1px)`,
        backgroundSize: '8px 8px'
      })
    },
    { 
      value: 'large-dots', 
      label: 'Large Dots',
      preview: (color, secondary) => ({
        backgroundColor: color,
        backgroundImage: `radial-gradient(${secondary}40 3px, transparent 3px)`,
        backgroundSize: '25px 25px'
      })
    },
    { 
      value: 'diagonal-lines', 
      label: 'Diagonal Lines',
      preview: (color, secondary) => ({
        backgroundColor: color,
        backgroundImage: `repeating-linear-gradient(45deg, ${secondary}22, ${secondary}22 2px, ${color} 2px, ${color} 8px)`
      })
    },
    { 
      value: 'horizontal-lines', 
      label: 'Horizontal Lines',
      preview: (color, secondary) => ({
        backgroundColor: color,
        backgroundImage: `repeating-linear-gradient(0deg, ${secondary}22, ${secondary}22 1px, ${color} 1px, ${color} 15px)`
      })
    },
    { 
      value: 'zigzag', 
      label: 'Zigzag Pattern',
      preview: (color, secondary) => ({
        backgroundColor: color,
        backgroundImage: `linear-gradient(135deg, ${secondary}22 25%, transparent 25%), linear-gradient(225deg, ${secondary}22 25%, transparent 25%), linear-gradient(45deg, ${secondary}22 25%, transparent 25%), linear-gradient(315deg, ${secondary}22 25%, ${color} 25%)`,
        backgroundPosition: '5px 0, 5px 0, 0 0, 0 0',
        backgroundSize: '10px 10px'
      })
    },
    { 
      value: 'triangles', 
      label: 'Triangle Mosaic',
      preview: (color, secondary) => ({
        backgroundColor: color,
        backgroundImage: `linear-gradient(45deg, ${secondary}22 50%, ${color} 50%)`,
        backgroundSize: '10px 10px'
      })
    },
    { 
      value: 'hexagon', 
      label: 'Hexagon Pattern',
      preview: (color, secondary) => ({
        backgroundColor: color,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='28' height='49' viewBox='0 0 28 49' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='${(secondary || '#ffffff').replace('#', '%23')}' fill-opacity='0.15'%3E%3Cpolygon points='13.99 1 24.245 6.525 24.245 17.575 13.99 23.1 3.735 17.575 3.735 6.525'/%3E%3C/g%3E%3C/svg%3E")`,
        backgroundSize: '28px 49px'
      })
    },
    { 
      value: 'waves', 
      label: 'Organic Waves',
      preview: (color, secondary) => ({
        backgroundColor: color,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='${(secondary || '#ffffff').replace('#', '%23')}' fill-opacity='0.2'%3E%3Cpath d='M30 30c0-16.569 13.431-30 30-30v60c-16.569 0-30-13.431-30-30z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      })
    },
    { 
      value: 'circuit', 
      label: 'Tech Circuit',
      preview: (color, secondary) => ({
        backgroundColor: color,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='20' viewBox='0 0 100 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='${(secondary || '#ffffff').replace('#', '%23')}' fill-opacity='0.15'%3E%3Cpath d='M0 0h100v20H0V0zm10 10a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm20 0a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm20 0a5 5 0 1 1 0-10 5 5 0 0 1 0 10z'/%3E%3C/g%3E%3C/svg%3E")`,
        backgroundSize: '100px 20px'
      })
    },
    { 
      value: 'noise', 
      label: 'Subtle Texture',
      preview: (color, secondary) => ({
        backgroundColor: color,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.08'/%3E%3C/svg%3E")`
      })
    },
    { 
      value: 'geometric', 
      label: 'Geometric Grid',
      preview: (color, secondary) => ({
        backgroundColor: color,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='${(secondary || '#ffffff').replace('#', '%23')}' fill-opacity='0.1'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2H0v-2h20v-2H0V8h20V6H0V4h20V2H0V0h22v20h2V0h2v20h2V0h2v20h2V0h2v20h2V0h2v22H20v-1.5z'/%3E%3C/g%3E%3C/svg%3E")`,
        backgroundSize: '40px 40px'
      })
    }
  ];

  // Visual elements that can be added to slides
  const visualElements = [
    { 
      value: 'none', 
      label: 'None',
      icon: '🚫'
    },
    { 
      value: 'corner-accent', 
      label: 'Corner Accent',
      icon: '📐'
    },
    { 
      value: 'side-bar', 
      label: 'Side Bar',
      icon: '📊'
    },
    { 
      value: 'top-border', 
      label: 'Top Border',
      icon: '📏'
    },
    { 
      value: 'geometric-shape', 
      label: 'Geometric Shape',
      icon: '🔷'
    },
    { 
      value: 'quote-marks', 
      label: 'Quote Marks',
      icon: '💬'
    },
    { 
      value: 'decorative-dots', 
      label: 'Decorative Dots',
      icon: '⚪'
    },
    { 
      value: 'arrow-accent', 
      label: 'Arrow Accent',
      icon: '➡️'
    }
  ];

  // Border styles
  const borderStyles = [
    { value: 'none', label: 'No Border' },
    { value: 'solid', label: 'Solid Border' },
    { value: 'dashed', label: 'Dashed Border' },
    { value: 'gradient', label: 'Gradient Border' },
    { value: 'shadow', label: 'Drop Shadow' },
    { value: 'glow', label: 'Soft Glow' },
    { value: 'rounded', label: 'Rounded Corners' },
    { value: 'outlined', label: 'Outlined Style' }
  ];

  // Nested CarouselSlidePreview component
  const CarouselSlidePreview = ({ 
    slideContent, 
    slideTitle,
    slideIndex,
    totalSlides,
    theme,
    fontPairing,
    titleFont,
    contentFont,
    backgroundStyle,
    visualElement = 'none',
    borderStyle = 'none',
    userName,
    userImage,
    previewRef,
    slideSize = 'square',
    isThumbnail = false,
    // New props for enhanced customization
    titleFontSize = 40,
    contentFontSize = 16,
    titleFontWeight = 700,
    contentFontWeight = 400,
    lineHeight = 1.4,
    letterSpacing = 0,
    textAlign = 'left',
    padding = 32,
    cornerRadius = 8,
    slideImage = null,
    imagePosition = 'top-right',
    imageSize = 100,
    imageOpacity = 1,
    imageRotation = 0,
    shadowIntensity = 10,
    gradientDirection = 135,
    borderWidth = 3,
    customColor1 = '#4890fe',
    customColor2 = '#ffffff',
    useCustomColors = false
  }) => {
    const currentTheme = slideThemes.find(t => t.value === theme) || slideThemes[0];
    const currentFonts = fontPairings.find(f => f.value === fontPairing) || fontPairings[0];
    const currentTitleFont = fontOptions.find(f => f.value === titleFont)?.family || currentFonts.heading;
    const currentContentFont = fontOptions.find(f => f.value === contentFont)?.family || currentFonts.body;
    
    // Use custom colors if enabled, otherwise use theme colors
    const primaryColor = useCustomColors ? customColor1 : currentTheme.primary;
    const secondaryColor = useCustomColors ? customColor2 : currentTheme.secondary;
    
    // Define aspect ratio based on slideSize
    const aspectRatio = slideSize === 'portrait' ? '4 / 5' : '1 / 1';

    // Generate background based on style
    const getBackgroundStyles = () => {
      const primary = primaryColor;
      const secondary = secondaryColor;
      const isDark = primary.startsWith('#0') || primary.startsWith('#1') || primary.startsWith('#2') || primary.startsWith('#3');
      
      switch(backgroundStyle) {
        case 'gradient-linear':
          return {
            background: `linear-gradient(${gradientDirection}deg, ${primary} 0%, ${primary}dd 100%)`,
            backgroundSize: '100% 100%'
          };
        case 'gradient-radial':
          return {
            background: `radial-gradient(circle, ${primary} 0%, ${primary}dd 100%)`,
            backgroundSize: '100% 100%'
          };
        case 'gradient-diagonal':
          return {
            background: `linear-gradient(${gradientDirection}deg, ${primary} 0%, ${primary}aa 50%, ${primary}dd 100%)`,
            backgroundSize: '100% 100%'
          };
        case 'grid':
          return {
            backgroundColor: primary,
            backgroundImage: `linear-gradient(${secondary}22 1px, transparent 1px), linear-gradient(90deg, ${secondary}22 1px, transparent 1px)`,
            backgroundSize: isThumbnail ? '5px 5px' : '20px 20px'
          };
        case 'dots':
          return {
            backgroundColor: primary,
            backgroundImage: `radial-gradient(${secondary}33 1px, transparent 1px)`,
            backgroundSize: isThumbnail ? '4px 4px' : '20px 20px'
          };
        case 'large-dots':
          return {
            backgroundColor: primary,
            backgroundImage: `radial-gradient(${secondary}40 3px, transparent 3px)`,
            backgroundSize: isThumbnail ? '12px 12px' : '25px 25px'
          };
        case 'diagonal-lines':
          return {
            backgroundColor: primary,
            backgroundImage: `repeating-linear-gradient(45deg, ${secondary}22, ${secondary}22 2px, ${primary} 2px, ${primary} 10px)`,
            backgroundSize: '100% 100%'
          };
        case 'horizontal-lines':
          return {
            backgroundColor: primary,
            backgroundImage: `repeating-linear-gradient(0deg, ${secondary}22, ${secondary}22 1px, ${primary} 1px, ${primary} 15px)`,
            backgroundSize: '100% 100%'
          };
        case 'zigzag':
          return {
            backgroundColor: primary,
            backgroundImage: `linear-gradient(135deg, ${secondary}22 25%, transparent 25%), linear-gradient(225deg, ${secondary}22 25%, transparent 25%), linear-gradient(45deg, ${secondary}22 25%, transparent 25%), linear-gradient(315deg, ${secondary}22 25%, ${primary} 25%)`,
            backgroundPosition: '10px 0, 10px 0, 0 0, 0 0',
            backgroundSize: isThumbnail ? '5px 5px' : '20px 20px'
          };
        case 'triangles':
          return {
            backgroundColor: primary,
            backgroundImage: `linear-gradient(45deg, ${secondary}22 50%, ${primary} 50%)`,
            backgroundSize: isThumbnail ? '5px 5px' : '20px 20px'
          };
        case 'hexagon':
          return {
            backgroundColor: primary,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='28' height='49' viewBox='0 0 28 49' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='${(secondary || '#ffffff').replace('#', '%23')}' fill-opacity='0.15'%3E%3Cpolygon points='13.99 1 24.245 6.525 24.245 17.575 13.99 23.1 3.735 17.575 3.735 6.525'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: isThumbnail ? '14px 24px' : '28px 49px'
          };
        case 'waves':
          return {
            backgroundColor: primary,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='${(secondary || '#ffffff').replace('#', '%23')}' fill-opacity='0.2'%3E%3Cpath d='M30 30c0-16.569 13.431-30 30-30v60c-16.569 0-30-13.431-30-30z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: isThumbnail ? '30px 30px' : '60px 60px'
          };
        case 'circuit':
          return {
            backgroundColor: primary,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='20' viewBox='0 0 100 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='${(secondary || '#ffffff').replace('#', '%23')}' fill-opacity='0.15'%3E%3Cpath d='M0 0h100v20H0V0zm10 10a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm20 0a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm20 0a5 5 0 1 1 0-10 5 5 0 0 1 0 10z'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: isThumbnail ? '50px 10px' : '100px 20px'
          };
        case 'noise':
          return {
            backgroundColor: primary,
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.08'/%3E%3C/svg%3E")`
          };
        case 'geometric':
          return {
            backgroundColor: primary,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='${(secondary || '#ffffff').replace('#', '%23')}' fill-opacity='0.1'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2H0v-2h20v-2H0V8h20V6H0V4h20V2H0V0h22v20h2V0h2v20h2V0h2v20h2V0h2v20h2V0h2v22H20v-1.5z'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: isThumbnail ? '20px 20px' : '40px 40px'
          };
        case 'solid':
        default:
          return {
            backgroundColor: primary
          };
      }
    };

    // Generate border styles
    const getBorderStyles = () => {
      const primary = primaryColor;
      const secondary = secondaryColor;
      
      switch(borderStyle) {
        case 'solid':
          return {
            border: `${borderWidth}px solid ${secondary}`
          };
        case 'dashed':
          return {
            border: `${borderWidth}px dashed ${secondary}`
          };
        case 'gradient':
          return {
            border: `${borderWidth}px solid transparent`,
            backgroundImage: `linear-gradient(${primary}, ${primary}), linear-gradient(45deg, ${secondary}, ${primary})`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box'
          };
        case 'shadow':
          return {
            boxShadow: `0 ${shadowIntensity}px ${shadowIntensity * 2}px rgba(0, 0, 0, 0.3)`
          };
        case 'glow':
          return {
            boxShadow: `0 0 ${shadowIntensity * 2}px ${secondary}66`
          };
        case 'rounded':
          return {
            borderRadius: '20px'
          };
        case 'outlined':
          return {
            border: `2px solid ${secondary}`,
            borderRadius: '12px'
          };
        case 'none':
        default:
          return {};
      }
    };

    // Generate visual elements
    const getVisualElementJSX = () => {
      const primary = primaryColor;
      const secondary = secondaryColor;
      const size = isThumbnail ? '8px' : '20px';
      
      switch(visualElement) {
        case 'corner-accent':
          return (
            <Box sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: isThumbnail ? '15px' : '40px',
              height: isThumbnail ? '15px' : '40px',
              background: `linear-gradient(135deg, ${secondary} 0%, ${primary} 100%)`,
              clipPath: 'polygon(100% 0, 0 0, 100% 100%)'
            }} />
          );
        case 'side-bar':
          return (
            <Box sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: isThumbnail ? '3px' : '8px',
              background: `linear-gradient(180deg, ${secondary} 0%, ${primary} 100%)`
            }} />
          );
        case 'top-border':
          return (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: isThumbnail ? '2px' : '5px',
              background: `linear-gradient(90deg, ${secondary} 0%, ${primary} 100%)`
            }} />
          );
        case 'geometric-shape':
          return (
            <Box sx={{
              position: 'absolute',
              top: isThumbnail ? '5px' : '20px',
              right: isThumbnail ? '5px' : '20px',
              width: isThumbnail ? '10px' : '30px',
              height: isThumbnail ? '10px' : '30px',
              background: secondary,
              borderRadius: '50%',
              opacity: 0.8
            }} />
          );
        case 'quote-marks':
          return (
            <Box sx={{
              position: 'absolute',
              top: isThumbnail ? '8px' : '25px',
              left: isThumbnail ? '8px' : '25px',
              fontSize: isThumbnail ? '8px' : '24px',
              color: secondary,
              opacity: 0.6,
              fontWeight: 'bold',
              fontFamily: 'serif'
            }}>
              "
            </Box>
          );
        case 'decorative-dots':
          return (
            <Box sx={{
              position: 'absolute',
              bottom: isThumbnail ? '5px' : '15px',
              right: isThumbnail ? '5px' : '15px',
              display: 'flex',
              gap: isThumbnail ? '2px' : '5px'
            }}>
              {[...Array(3)].map((_, i) => (
                <Box key={i} sx={{
                  width: isThumbnail ? '3px' : '8px',
                  height: isThumbnail ? '3px' : '8px',
                  borderRadius: '50%',
                  background: secondary,
                  opacity: 0.7
                }} />
              ))}
            </Box>
          );
        case 'arrow-accent':
          return (
            <Box sx={{
              position: 'absolute',
              bottom: isThumbnail ? '5px' : '20px',
              left: isThumbnail ? '5px' : '20px',
              fontSize: isThumbnail ? '6px' : '18px',
              color: secondary,
              opacity: 0.8
            }}>
              →
            </Box>
          );
        case 'none':
        default:
          return null;
      }
    };

    // Function to render slide image
    const getSlideImageJSX = () => {
      if (!slideImage || imagePosition === 'background') return null;
      
      const getImagePositionStyles = () => {
        const size = isThumbnail ? imageSize / 4 : imageSize;
        const baseStyles = {
          position: 'absolute',
          width: `${size}px`,
          height: `${size}px`,
          objectFit: 'cover',
          borderRadius: '8px',
          opacity: imageOpacity,
          transform: `rotate(${imageRotation}deg)`,
          zIndex: 5
        };
        
        switch(imagePosition) {
          case 'top-left':
            return { ...baseStyles, top: '20px', left: '20px' };
          case 'top-right':
            return { ...baseStyles, top: '20px', right: '20px' };
          case 'bottom-left':
            return { ...baseStyles, bottom: '20px', left: '20px' };
          case 'bottom-right':
            return { ...baseStyles, bottom: '20px', right: '20px' };
          case 'center':
            return { 
              ...baseStyles, 
              top: '50%', 
              left: '50%', 
              transform: `translate(-50%, -50%) rotate(${imageRotation}deg)` 
            };
          default:
            return { ...baseStyles, top: '20px', right: '20px' };
        }
      };
      
      return (
        <Box
          component="img"
          src={slideImage}
          alt="Slide image"
          sx={getImagePositionStyles()}
        />
      );
    };
    
    const backgroundStyles = getBackgroundStyles();
    const borderStyles = getBorderStyles();
    
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0
        }}
      >
        <Box
          ref={previewRef}
          className="carousel-slide-preview"
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            aspectRatio: aspectRatio,
            ...backgroundStyles,
            ...borderStyles,
            borderRadius: borderStyle === 'rounded' ? '20px' : borderStyle === 'outlined' ? '12px' : `${cornerRadius}px`,
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            boxShadow: borderStyle === 'shadow' ? `0 ${shadowIntensity}px ${shadowIntensity * 2}px rgba(0, 0, 0, 0.3)` : borderStyle === 'glow' ? `0 0 ${shadowIntensity * 2}px ${secondaryColor}66` : '0 4px 20px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: isThumbnail ? '10px' : `${padding}px`,
            ...(slideImage && imagePosition === 'background' && {
              backgroundImage: `url(${slideImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: `${primaryColor}${Math.round((1 - imageOpacity) * 255).toString(16).padStart(2, '0')}`,
                zIndex: 1
              }
            })
          }}
        >
          {/* Profile Badge */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: isThumbnail ? 0.3 : 0,
            px: isThumbnail ? 1 : 0,
            zIndex: slideImage && imagePosition === 'background' ? 2 : 'auto',
            position: 'relative'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isThumbnail ? 0.5 : 1.5 }}>
              <Avatar 
                src={userImage} 
                sx={{ 
                  width: isThumbnail ? 12 : 36, 
                  height: isThumbnail ? 12 : 36 
                }}
              >
                {!userImage && (userName?.[0]?.toUpperCase() || 'U')}
              </Avatar>
              <Typography 
                sx={{ 
                  color: currentTheme.secondary,
                  fontSize: isThumbnail ? '5px' : '14px',
                  fontWeight: 500,
                  fontFamily: currentContentFont
                }}
              >
                {userName || 'Your Name'}
              </Typography>
            </Box>
            <Typography 
              sx={{ 
                color: currentTheme.secondary,
                fontSize: isThumbnail ? '5px' : '14px',
                opacity: 0.8,
                fontFamily: currentContentFont
              }}
            >
              {slideIndex + 1}/{totalSlides}
            </Typography>
          </Box>
          
          {/* Main Content */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: isThumbnail ? 'flex-start' : 'center',
            my: isThumbnail ? 0.3 : 4,
            px: isThumbnail ? 1 : 0,
            overflowY: 'hidden',
            height: isThumbnail ? 'calc(100% - 20px)' : 'auto'
          }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: secondaryColor,
                fontWeight: isThumbnail ? 700 : titleFontWeight,
                fontSize: isThumbnail ? '8px' : `${titleFontSize}px`,
                marginBottom: isThumbnail ? '0.6rem' : '1rem',
                fontFamily: currentTitleFont,
                lineHeight: isThumbnail ? 1.2 : lineHeight,
                textAlign: isThumbnail ? 'left' : textAlign,
                letterSpacing: isThumbnail ? 0 : `${letterSpacing}px`,
                zIndex: slideImage && imagePosition === 'background' ? 2 : 'auto',
                position: 'relative',
                display: '-webkit-box',
                WebkitLineClamp: isThumbnail ? 2 : 'none',
                WebkitBoxOrient: 'vertical',
                '& p': { margin: 0, padding: 0 },
                '& .ql-size-small': { fontSize: isThumbnail ? '6px' : '1.8rem' },
                '& .ql-size-large': { fontSize: isThumbnail ? '10px' : '3rem' },
                '& .ql-size-huge': { fontSize: isThumbnail ? '12px' : '3.5rem' },
                '& .ql-align-center': { textAlign: 'center' },
                '& .ql-align-right': { textAlign: 'right' },
                '& .ql-align-justify': { textAlign: 'justify' },
                ...(isThumbnail && {
                  flex: '0 0 auto',
                  maxHeight: '28px',
                  marginTop: '12px'
                })
              }}
              dangerouslySetInnerHTML={{ __html: slideTitle ? slideTitle : 'Your Title Here' }}
            />
            
            <Box 
              sx={{ 
                color: secondaryColor,
                fontSize: isThumbnail ? '6px' : `${contentFontSize}px`,
                lineHeight: isThumbnail ? 1.2 : lineHeight,
                opacity: 0.9,
                fontFamily: currentContentFont,
                fontWeight: isThumbnail ? 400 : contentFontWeight,
                textAlign: isThumbnail ? 'left' : textAlign,
                letterSpacing: isThumbnail ? 0 : `${letterSpacing}px`,
                zIndex: slideImage && imagePosition === 'background' ? 2 : 'auto',
                position: 'relative',
                '& p': { margin: 0, padding: 0 },
                '& ul, & ol': { 
                  paddingLeft: isThumbnail ? '1em' : '1.5em', 
                  marginTop: isThumbnail ? '0.15em' : '0.5em', 
                  marginBottom: isThumbnail ? '0.15em' : '0.5em',
                  '& li': {
                    marginBottom: isThumbnail ? '0.15em' : '0.3em'
                  }
                },
                '& .ql-size-small': { fontSize: isThumbnail ? '6px' : '0.75em' },
                '& .ql-size-large': { fontSize: isThumbnail ? '9px' : '1.5em' },
                '& .ql-size-huge': { fontSize: isThumbnail ? '11px' : '2.5em' },
                '& .ql-font-arial': { fontFamily: 'Arial, sans-serif' },
                '& .ql-font-courier-new': { fontFamily: "'Courier New', Courier, monospace" },
                '& .ql-font-georgia': { fontFamily: 'Georgia, serif' },
                '& .ql-font-helvetica': { fontFamily: 'Helvetica, sans-serif' },
                '& .ql-font-tahoma': { fontFamily: 'Tahoma, sans-serif' },
                '& .ql-font-times-new-roman': { fontFamily: "'Times New Roman', Times, serif" },
                '& .ql-font-verdana': { fontFamily: 'Verdana, sans-serif' },
                whiteSpace: 'pre-wrap',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: isThumbnail ? 4 : 'none',
                WebkitBoxOrient: 'vertical',
                maxHeight: isThumbnail ? '70px' : 'none',
                ...(isThumbnail && {
                  marginTop: '5px',
                  flex: '1 1 auto',
                  paddingTop: '1px'
                })
              }}
              dangerouslySetInnerHTML={{ __html: slideContent ? slideContent : 'Add content to your slide' }}
            />
          </Box>
          
          {/* Visual Elements */}
          {getVisualElementJSX()}
          
          {/* Slide Image */}
          {getSlideImageJSX()}
        </Box>
      </Box>
    );
  };

  // Prevent unnecessary re-renders by memoizing the slide objects
  const memoizedSlides = useMemo(() => slides, [
    // Only recreate when the number of slides changes or when a slide changes
    slides.length,
    // Create a string representation of slides to detect content changes
    JSON.stringify(slides.map(slide => ({
      title: slide.title,
      contentHash: typeof slide.content === 'string' ? slide.content.length : 0
    })))
  ]);

  // Initialize ResizeObserver with debouncing
  useEffect(() => {
    // Cleanup function for the timeout
    const clearResizeTimeout = () => {
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
        resizeTimeout.current = null;
      }
    };
    
    // Create ResizeObserver with debounced callback
    resizeObserver.current = new ResizeObserver(entries => {
      clearResizeTimeout();
      
      // Set a timeout to handle resize after a delay
      resizeTimeout.current = setTimeout(() => {
        console.log('Resized elements processed with debounce');
      }, 100); // 100ms debounce
    });
    
    // Observe elements that might cause the ResizeObserver loop error
    const previewContainer = document.querySelector('.carousel-slide-preview-container');
    if (previewContainer) {
      resizeObserver.current.observe(previewContainer);
    }
    
    // Cleanup
    return () => {
      clearResizeTimeout();
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, []);

  // Load slides from localStorage when the component mounts
  useEffect(() => {
    if (isModalMode && initialContent) {
      setTopic(initialContent);
      // Optionally, trigger generation automatically or switch to a relevant tab
      // handleGenerate(); // Or setActiveTab('edit'); if slides are pre-filled
      // For now, we just set the topic and user can click generate
      setActiveTab('generate');
    } else if (!isModalMode) {
      // Existing logic for non-modal mode (e.g., loading from localStorage)
      const storedSlides = localStorage.getItem('carouselSlides');
      if (storedSlides) {
        try {
          const parsedSlides = JSON.parse(storedSlides);
          if (Array.isArray(parsedSlides) && parsedSlides.length > 0) {
            setSlides(parsedSlides);
            setTotalSlides(parsedSlides.length);
            setActiveTab('edit'); // Switch to edit tab if slides are loaded
          } else {
            // If storedSlides is not an array or is empty, set example slides
            const deepCopy = JSON.parse(JSON.stringify(exampleSlides));
            setSlides(deepCopy);
            setTotalSlides(deepCopy.length);
            setActiveTab('edit');
          }
        } catch (e) {
          console.error("Error parsing stored slides:", e);
          const deepCopy = JSON.parse(JSON.stringify(exampleSlides));
          setSlides(deepCopy);
          setTotalSlides(deepCopy.length);
          setActiveTab('edit');
        }
        localStorage.removeItem('carouselSlides');
      } else {
        // Set default example slides directly if nothing is stored
        const deepCopy = JSON.parse(JSON.stringify(exampleSlides));
        setSlides(deepCopy);
        setTotalSlides(deepCopy.length);
        setActiveTab('edit'); // Default to edit tab with example slides
      }
    }
    setComponentMounted(true);
  }, [isModalMode, initialContent, location.search]); // Added dependencies

  // Handle clearing all content and replacing with a single default slide
  const handleClearAllContent = () => {
    const defaultSlide = {
      title: '<p>Add Title</p>',
      content: '<p>Add Content</p>'
    };
    
    // Replace all slides with just the default slide
    setSlides([defaultSlide]);
    setTotalSlides(1);
    setCurrentSlideIndex(0);
    enqueueSnackbar('All content cleared', { variant: 'info' });
  };

  // Handle title change
  const handleTitleChange = (content) => {
    // Don't update if content hasn't actually changed
    if (slides[currentSlideIndex]?.title === content) {
      return;
    }
    
    // Create a fresh copy of the slides array with immutable update pattern
    const newSlides = slides.map((slide, idx) => {
      if (idx === currentSlideIndex) {
        // Only update the current slide's title
        return {
          ...slide,
          title: content
        };
      }
      // Return other slides unchanged
      return { ...slide };
    });
    
    // Update state with the new slides array
    setSlides(newSlides);
  };

  // Handle content change
  const handleContentChange = (content) => {
    // Don't update if content hasn't actually changed
    if (slides[currentSlideIndex]?.content === content) {
      return;
    }
    
    // Create a fresh copy of the slides array to avoid reference issues
    const newSlides = slides.map((slide, idx) => {
      if (idx === currentSlideIndex) {
        // Only update the current slide
        return {
          ...slide,
          content: content // Store the raw HTML content to preserve formatting
        };
      }
      // Return other slides unchanged
      return { ...slide };
    });
    
    // Update state with the new slides array
    setSlides(newSlides);
  };

  // Add a new slide
  const handleAddSlide = () => {
    const newSlides = [...slides, { title: '<p>New slide</p>', content: '<p>Add content here</p>' }];
    setSlides(newSlides);
    setCurrentSlideIndex(newSlides.length - 1);
    setTotalSlides(newSlides.length);
  };

  // Delete the current slide
  const handleDeleteSlide = () => {
    if (slides.length <= 1) return;
    
    const newSlides = slides.filter((_, index) => index !== currentSlideIndex);
    setSlides(newSlides);
    setCurrentSlideIndex(Math.min(currentSlideIndex, newSlides.length - 1));
    setTotalSlides(newSlides.length);
  };

  // Navigate to the previous slide
  const goToPrevSlide = () => {
    if (currentSlideIndex > 0) {
      // Create transition effect
      const previewContainer = document.querySelector('.carousel-slide-preview-container');
      if (previewContainer) {
        // Apply slide-from-left effect
        previewContainer.style.transition = 'none';
        previewContainer.style.opacity = '0';
        previewContainer.style.transform = 'translateX(-100px)';
        
        // Force reflow
        void previewContainer.offsetWidth;
        
        setCurrentSlideIndex(prevIndex => prevIndex - 1);
        
        // Animate to normal position
        previewContainer.style.transition = 'all 0.3s ease-out';
        previewContainer.style.opacity = '1';
        previewContainer.style.transform = 'translateX(0)';
      } else {
        setCurrentSlideIndex(prevIndex => prevIndex - 1);
      }
    }
  };

  // Navigate to the next slide
  const goToNextSlide = () => {
    if (currentSlideIndex < slides.length - 1) {
      // Create transition effect
      const previewContainer = document.querySelector('.carousel-slide-preview-container');
      if (previewContainer) {
        // Apply slide-from-right effect
        previewContainer.style.transition = 'none';
        previewContainer.style.opacity = '0';
        previewContainer.style.transform = 'translateX(100px)';
        
        // Force reflow
        void previewContainer.offsetWidth;
        
        setCurrentSlideIndex(prevIndex => prevIndex + 1);
        
        // Animate to normal position
        previewContainer.style.transition = 'all 0.3s ease-out';
        previewContainer.style.opacity = '1';
        previewContainer.style.transform = 'translateX(0)';
      } else {
        setCurrentSlideIndex(prevIndex => prevIndex + 1);
      }
    }
  };

  // Handle theme change
  const handleThemeChange = (e) => {
    setTheme(e.target.value);
  };

  // Handle font pairing change
  const handleFontPairingChange = (e) => {
    setFontPairing(e.target.value);
  };

  // Handle title font change
  const handleTitleFontChange = (e) => {
    setTitleFont(e.target.value);
  };

  // Handle content font change
  const handleContentFontChange = (e) => {
    setContentFont(e.target.value);
  };

  // Handle downloading the current slide as an image
  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      
      // Create a new PDF document
      const pdf = new jsPDF({
        orientation: slideSize === 'portrait' ? 'portrait' : 'landscape',
        unit: 'px',
        format: slideSize === 'portrait' ? [850, 1100] : [1100, 850]
      });
      
      // Save current slide index to restore it later
      const originalSlideIndex = currentSlideIndex;
      
      // Generate PDF pages for all slides
      for (let i = 0; i < slides.length; i++) {
        // Set current slide index to capture each slide
        setCurrentSlideIndex(i);
        
        // Wait for slide to render
        await new Promise(resolve => setTimeout(resolve, 200));
        
        let slideElement = document.querySelector('.carousel-slide-preview');
        if (!slideElement) {
          throw new Error('Carousel slide preview element not found');
        }
        
        // Generate canvas from the slide
        const canvas = await html2canvas(slideElement, {
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
          backgroundColor: null
        });
        
        // Get the image data
        const imgData = canvas.toDataURL('image/png');
        
        // Add a new page for each slide (except the first one)
        if (i > 0) {
          pdf.addPage();
        }
        
        // Add the image to the PDF
        pdf.addImage(
          imgData, 
          'PNG', 
          0, 
          0, 
          pdf.internal.pageSize.getWidth(), 
          pdf.internal.pageSize.getHeight(),
          `slide-${i+1}`,
          'FAST'
        );
      }
      
      // Restore original slide index
      setCurrentSlideIndex(originalSlideIndex);
      
      // Save the PDF
      pdf.save(`carousel-slides-${Date.now()}.pdf`);
      
      // setNotification({ open: true, message: 'PDF downloaded successfully!', severity: 'success' });
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating carousel PDF:', error);
      setError('Failed to download carousel: ' + error.message);
    } finally {
      setDownloading(false);
    }
  };

  // Handle saving all slides as a post
  const handleSaveCarousel = async () => {
    try {
      setSaving(true);
      
      // Generate images for all slides
      const slideImages = [];
      
      for (let i = 0; i < slides.length; i++) {
        setCurrentSlideIndex(i);
        
        // Small delay to ensure the slide is rendered
        await new Promise(resolve => setTimeout(resolve, 100));
        
        let previewContainer = null;
        
        if (previewRef && previewRef.current) {
          previewContainer = previewRef.current;
        }
        
        if (!previewContainer) {
          throw new Error(`Carousel slide preview container not found for slide ${i + 1}`);
        }
        
        // Generate the image
        const dataUrl = await toPng(previewContainer, {
          quality: 1.0,
          pixelRatio: 3,
          cacheBust: true,
          skipFonts: false,
          width: 1080,
          height: slideSize === 'portrait' ? 1350 : 1080
        });
        
        // Convert to blob
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        slideImages.push(blob);
      }
      
      console.log('Saving carousel with slides:', slideImages?.length);
        setSaveSuccess(true);
        setOpenSuccessDialog(true);
        refetchCredits();
      
    } catch (error) {
      console.error('Error preparing carousel slides:', error);
      setError('Failed to save carousel: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Sync editor content with current slide when index changes
  useEffect(() => {
    // Use a ref to track whether we're in the middle of a programmatic update
    let isUpdating = false;
    
    // Clear any pending timeouts to avoid race conditions
    const timeoutId = setTimeout(() => {
      if (isUpdating) return;
      
      try {
        isUpdating = true;
        
        // Handle content editor sync - only if editor is available and not currently being edited
        if (currentSlideIndex >= 0 && currentSlideIndex < memoizedSlides.length && editorRef.current) {
          const currentContent = memoizedSlides[currentSlideIndex].content;
          
          // Check if content needs updating
          if (typeof currentContent === 'string') {
            // For manual content editing, we'll let the onChange handler manage state
            // We only force updates on slide changes
            if (editorRef.current && editorRef.current.getEditor) {
              if (editorRef.current.getEditor().hasFocus()) {
                // If the editor has focus, don't override what the user is typing
                console.log('Content editor has focus, skipping sync');
              } else {
                // Only update when the content truly differs to avoid loops
                const editorContent = editorRef.current.getEditor().root.innerHTML;
                if (editorContent !== currentContent) {
                  console.log('Syncing content editor');
                  editorRef.current.getEditor().root.innerHTML = currentContent;
                }
              }
            }
          }
        }
      } catch (err) {
        console.error('Error in editor sync effect:', err);
      } finally {
        isUpdating = false;
      }
    }, 50);
    
    // Clean up timeout on unmount or when dependencies change
    return () => clearTimeout(timeoutId);
  }, [currentSlideIndex, memoizedSlides]);

  // Set up keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Get the active element
      const activeElement = document.activeElement;
      
      // Check if we're in an editor
      const isInEditor = activeElement && (
        // Check if inside any contenteditable element
        activeElement.getAttribute('contenteditable') === 'true' ||
        // Check if inside an input, textarea or editable area
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        // Check if inside a Quill editor component
        activeElement.closest('.ql-editor') ||
        activeElement.closest('.ql-container')
      );
      
      // Only apply keyboard navigation if not in an editor
      if (!isInEditor) {
        if (e.key === 'ArrowLeft') {
          goToPrevSlide();
        } else if (e.key === 'ArrowRight') {
          goToNextSlide();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentSlideIndex, slides.length]); // Re-add event listener when these dependencies change

  // Add this new function
  const handleGenerate = async () => {
    const content = inputMode === 'topic' ? topic : inputText;
    
    if (!content) {
      enqueueSnackbar(`Please enter ${inputMode === 'topic' ? 'a topic' : 'text to convert'}`, { variant: 'error' });
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/ai/generate-carousel`,
        { 
          content: content,
          mode: inputMode
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const { success, slides } = response.data;
      
      if (success && slides && slides.length > 0) {
        const formattedSlides = slides.map(slide => ({
          title: `<p>${slide.title || ''}</p>`,
          content: slide.content || ''
        }));

        setSlides(formattedSlides);
        setTotalSlides(formattedSlides.length);
        setCurrentSlideIndex(0);
        setActiveTab('edit');
        enqueueSnackbar('Carousel generated successfully!', { variant: 'success' });
      } else {
        throw new Error('No slides received from the server');
      }
    } catch (error) {
      console.error('Error generating carousel:', error);
      setError(error.response?.data?.message || 'Failed to generate carousel. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Handle background style change
  const handleBackgroundStyleChange = (e) => {
    setBackgroundStyle(e.target.value);
  };

  const handleVisualElementChange = (e) => {
    setVisualElement(e.target.value);
  };

  const handleBorderStyleChange = (e) => {
    setBorderStyle(e.target.value);
  };

  // Image handling functions
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageUrl = reader.result;
        setSlideImages(prev => ({
          ...prev,
          [currentSlideIndex]: imageUrl
        }));
        setCurrentSlideImage(imageUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSlideImages(prev => {
      const newImages = { ...prev };
      delete newImages[currentSlideIndex];
      return newImages;
    });
    setCurrentSlideImage(null);
  };

  // Sync current slide image when slide index changes
  useEffect(() => {
    setCurrentSlideImage(slideImages[currentSlideIndex] || null);
  }, [currentSlideIndex, slideImages]);

  // Add useEffect to fetch user profile and LinkedIn data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        const userData = response.data.user;
        setUserProfile(userData);

        if (userData.linkedinProfile) {
          // Fetch LinkedIn profile data
          const linkedinResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/linkedin/profile-data`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          setLinkedinProfileData(linkedinResponse.data);
          // Update display name and profile image from LinkedIn data
          setDisplayName(linkedinResponse.data.name || userData.name);
          setProfileImage(linkedinResponse.data.profile_picture || userData.image);
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
      }
    };

    fetchUserProfile();
  }, []);

  // Add function to fetch posts
  const fetchPosts = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/posts`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setAvailablePosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setError('Failed to fetch posts');
    }
  };

  // Handle attaching carousel to post
  const handleAttachToPost = async (postId) => {
    try {
      setSaving(true);
      setError(null);
      
      // Create a new PDF document
      const pdf = new jsPDF({
        orientation: slideSize === 'portrait' ? 'portrait' : 'landscape',
        unit: 'px',
        format: slideSize === 'portrait' ? [850, 1100] : [1100, 850]
      });
      
      // Save current slide index to restore it later
      const originalSlideIndex = currentSlideIndex;
      
      // Generate PDF pages for all slides
      for (let i = 0; i < slides.length; i++) {
        // Set current slide index to capture each slide
        setCurrentSlideIndex(i);
        
        // Wait for slide to render
        await new Promise(resolve => setTimeout(resolve, 200));
        
        let slideElement = document.querySelector('.carousel-slide-preview');
        if (!slideElement) {
          throw new Error('Carousel slide preview element not found');
        }
        
        // Generate canvas from the slide
        const canvas = await html2canvas(slideElement, {
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
          backgroundColor: null
        });
        
        // Get the image data
        const imgData = canvas.toDataURL('image/png');
        
        // Add a new page for each slide (except the first one)
        if (i > 0) {
          pdf.addPage();
        }
        
        // Add the image to the PDF
        pdf.addImage(
          imgData, 
          'PNG', 
          0, 
          0, 
          pdf.internal.pageSize.getWidth(), 
          pdf.internal.pageSize.getHeight(),
          `slide-${i+1}`,
          'FAST'
        );
      }
      
      // Restore original slide index
      setCurrentSlideIndex(originalSlideIndex);
      
      // Get the PDF as blob
      const pdfBlob = pdf.output('blob');
      
      // Create FormData and append the PDF
      const formData = new FormData();
      formData.append('file', pdfBlob, `carousel-${Date.now()}.pdf`);

      // Send to backend
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/posts/${postId}/attachment`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Close the dialog and show success message
      setPostsDialogOpen(false);
      enqueueSnackbar('Carousel successfully attached to post!', { 
        variant: 'success',
        autoHideDuration: 4000,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center'
        }
      });
      
      // Clear any previous success message state
      setSuccessMessage('');
      
    } catch (error) {
      console.error('Error attaching carousel to post:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to attach carousel to post', { 
        variant: 'error',
        autoHideDuration: 4000,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center'
        }
      });
    } finally {
      setSaving(false);
    }
  };

  // UPDATED FOR MODAL MODE
  const handleUseThisCarousel = async () => {
    if (!onCarouselGenerated || !onClose) return;

    try {
      setDownloading(true); // Use downloading state for loading indication
      setError(null);

      const pdf = new jsPDF({
        orientation: slideSize === 'portrait' ? 'portrait' : 'landscape',
        unit: 'px',
        format: slideSize === 'portrait' ? [850, 1100] : [1100, 850]
      });
      
      const originalSlideIndex = currentSlideIndex;
      
      for (let i = 0; i < slides.length; i++) {
        setCurrentSlideIndex(i);
        await new Promise(resolve => setTimeout(resolve, 200));
        
        let slideElement = document.querySelector('.carousel-slide-preview');
        if (!slideElement) {
          throw new Error('Carousel slide preview element not found for PDF generation.');
        }
        
        const canvas = await html2canvas(slideElement, {
          scale: 2, logging: false, useCORS: true, allowTaint: true, backgroundColor: null
        });
        
        const imgData = canvas.toDataURL('image/png');
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight(), `slide-${i+1}`, 'FAST');
      }
      
      setCurrentSlideIndex(originalSlideIndex);
      const pdfBlob = pdf.output('blob');
      
      onCarouselGenerated(pdfBlob);
      onClose();

    } catch (error) {
      console.error('Error generating carousel PDF for modal:', error);
      setError('Failed to generate carousel for attachment: ' + error.message);
      enqueueSnackbar('Error generating carousel: ' + error.message, { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  if (!componentMounted) {
    return <LoadingScreen message="Loading carousel editor..." />;
  }

  return (
    <Container maxWidth="xl" className={!isModalMode ? "page-container" : ""} sx={isModalMode ? {pt: 1, height:'100%', display:'flex', flexDirection:'column'} : {}}>
      {!isModalMode && (
        <Helmet>
          <title>LinkedIn Carousel Generator | Instation</title>
          <meta name="description" content="Create engaging carousel posts for LinkedIn with our AI-powered carousel generator." />
        </Helmet>
      )}
      
      <Box className={!isModalMode ? "content-container" : ""} sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Grid container spacing={isModalMode ? 2 : 3} className="content-grid" sx={{flexGrow: 1, overflow: 'hidden'}}>
          {/* Left Panel - Editing Controls */}
          <Grid item xs={12} md={isModalMode ? 5 : 5} lg={isModalMode ? 4 : 4} className="content-panel" sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            <Paper 
              elevation={0} 
              className="content-paper"
              sx={{ 
                // Override base content-paper styles for sticky header structure
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden', // Paper itself does not scroll
                height: '100%',
                // minHeight: 'calc(100vh - 100px)', // Removed
                borderRadius: 2, 
                // overflow: 'auto' // Removed
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 1,
                flexShrink: 0 // Sticky Part 1
              }}>
                <Typography variant="h6" className="component-heading" sx={{ mb: 1 }}>
                  Create Carousel
                </Typography>
              </Box>

              {/* Error & Success Alerts - Keep in sticky part? Or move to scrollable? Let's keep sticky for now */}
              <Box sx={{ flexShrink: 0, mb: 2 /* Add margin if kept sticky */ }}>
                {error && (
                  <Alert
                    severity="error"
                    sx={{ borderRadius: '8px', mb: 1 /* Adjusted margin */ }}
                    onClose={() => setError(null)}
                  >
                    {error}
                  </Alert>
                )}
                {saveSuccess && !error && (
                  <Alert
                    severity="success"
                    sx={{ borderRadius: '8px', mb: 1 /* Adjusted margin */ }}
                    onClose={() => setSaveSuccess(false)}
                  >
                    Your carousel has been saved successfully!
                  </Alert>
                )}
              </Box>

              {/* Tabs - Sticky Part 2 */}
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  mb: 3,
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'var(--primary-color)',
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    color: 'var(--text-secondary)',
                    '&.Mui-selected': {
                      color: 'var(--primary-color)',
                      fontWeight: 600,
                    },
                    minWidth: '100px',
                    padding: '10px 16px',
                  },
                  flexShrink: 0 // Make Tabs sticky
                }}
              >
                <Tab value="generate" label="Generate" />
                <Tab value="edit" label="Edit Content" />
                <Tab value="customize" label="Customize" />
              </Tabs>
              
              {/* New Scrollable Content Area for Left Panel */}
              <Box
                sx={{
                  flex: 1, 
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pr: 1, 
                  pt: 1, 
                  minHeight: 0,
                  maxHeight: 'calc(100vh - 250px)', // Constrain height to enable scrolling
                  // Custom scrollbar styling
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(0,0,0,0.1)',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(72, 144, 254, 0.3)',
                    borderRadius: '3px',
                    '&:hover': {
                      background: 'rgba(72, 144, 254, 0.5)',
                    },
                  },
                  // Specific padding for modal mode to use space better
                  ...(isModalMode && { 
                    pl: 1, 
                    pr: 0.5, 
                    pt: 0.5,
                    maxHeight: 'calc(100vh - 180px)' // Adjust for modal
                  })
                }}
              >
                {/* Generate Tab */}
                {activeTab === 'generate' && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 2,
                    pb: 2 // Add bottom padding for better scrolling
                  }}>
                    {/* Input Mode Selection */}
                    <Box>
                      <Typography
                        variant="subtitle1"
                        className="section-title"
                        style={{ fontWeight: 500, color: 'black' }}
                        sx={{ mb: 2 }}
                      >
                        Generation Mode
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Button
                          variant={inputMode === 'topic' ? 'contained' : 'outlined'}
                          onClick={() => setInputMode('topic')}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            bgcolor: inputMode === 'topic' ? 'var(--primary-color)' : 'transparent',
                            borderColor: 'var(--primary-color)',
                            color: inputMode === 'topic' ? 'white' : 'var(--primary-color)',
                            '&:hover': {
                              bgcolor: inputMode === 'topic' ? 'var(--primary-dark)' : 'rgba(72, 144, 254, 0.04)',
                              borderColor: 'var(--primary-color)'
                            }
                          }}
                        >
                          Generate from Topic
                        </Button>
                        <Button
                          variant={inputMode === 'text' ? 'contained' : 'outlined'}
                          onClick={() => setInputMode('text')}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            bgcolor: inputMode === 'text' ? 'var(--primary-color)' : 'transparent',
                            borderColor: 'var(--primary-color)',
                            color: inputMode === 'text' ? 'white' : 'var(--primary-color)',
                            '&:hover': {
                              bgcolor: inputMode === 'text' ? 'var(--primary-dark)' : 'rgba(72, 144, 254, 0.04)',
                              borderColor: 'var(--primary-color)'
                            }
                          }}
                        >
                          Convert Text to Carousel
                        </Button>
                      </Box>
                    </Box>

                    {/* Input Field */}
                    <Box>
                      <Typography
                        variant="subtitle1"
                        className="section-title"
                        style={{ fontWeight: 500, color: 'black' }}
                        sx={{ mb: 1 }}
                      >
                        {inputMode === 'topic' ? 'Topic' : 'Text Content'}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={inputMode === 'text' ? 8 : 4}
                        value={inputMode === 'topic' ? topic : inputText}
                        onChange={(e) => inputMode === 'topic' ? setTopic(e.target.value) : setInputText(e.target.value)}
                        placeholder={
                          inputMode === 'topic' 
                            ? "Enter a topic or idea for your carousel. Be specific to get better results. Example: 'The future of remote work and its impact on company culture'"
                            : "Paste your existing text here and AI will convert it into a carousel format. This can be a blog post, article, or any long-form content you want to break down into slides."
                        }
                        variant="outlined"
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            '&:hover fieldset': {
                              borderColor: 'var(--primary-color)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'var(--primary-color)',
                            }
                          }
                        }}
                      />
                    </Box>

                    <Button
                      variant="contained"
                      onClick={handleGenerate}
                      disabled={generating || (inputMode === 'topic' ? !topic : !inputText)}
                      startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                      sx={{
                        mt: 1,
                        bgcolor: 'var(--primary-color)',
                        '&:hover': {
                          bgcolor: 'var(--primary-dark)',
                        },
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.2
                      }}
                    >
                      {generating ? 'Generating...' : (
                        <>
                          {inputMode === 'topic' ? 'Generate Carousel' : 'Convert to Carousel'}
                          <Typography component="span" sx={{
                            ml: 1,
                            fontSize: '0.75rem',
                            color: ((inputMode === 'topic' ? !topic : !inputText) && slides.length === 0) ? 'rgba(255, 255, 255, 0.5)' : 'white'
                          }}>
                            (2 credits)
                          </Typography>
                        </>
                      )}
                    </Button>

                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                      {inputMode === 'topic' 
                        ? 'The AI will generate a carousel of slides based on your topic. You can then edit and customize the content in the other tabs.'
                        : 'The AI will analyze your text and intelligently break it down into a series of carousel slides, maintaining the key points and flow.'
                      }
                    </Typography>
                  </Box>
                )}
                
                {/* Edit Content Tab */}
                {activeTab === 'edit' && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 2,
                    pb: 2 // Add bottom padding for better scrolling
                  }}>
                    {/* Title Input */}
                    <Box>
                      <Typography
                        variant="subtitle1"
                        className="section-title"
                        style={{ fontWeight: 500, color: 'black' }}
                        sx={{ mb: 1 }}
                      >
                        Slide Title
                      </Typography>
                      {memoizedSlides.length > 0 && (
                        <Box sx={{ 
                          marginBottom: 2,
                          '& .ql-toolbar': {
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px',
                            borderColor: 'rgba(0, 0, 0, 0.1)',
                            borderBottom: 'none'
                          },
                          '& .ql-container': {
                            borderBottomLeftRadius: '8px',
                            borderBottomRightRadius: '8px',
                            borderColor: 'rgba(0, 0, 0, 0.1)'
                          },
                          '& .ql-editor': {
                            minHeight: '60px',
                            padding: '12px 15px'
                          }
                        }}
                        className="quill-title-container"
                      >
                        <ReactQuill
                          ref={titleEditorRef}
                          key={`title-editor-${currentSlideIndex}`}
                          theme="snow"
                          value={memoizedSlides[currentSlideIndex]?.title || ''}
                          onChange={handleTitleChange}
                          modules={titleQuillModules}
                          formats={titleQuillFormats}
                          placeholder="Enter slide title..."
                          preserveWhitespace={true}
                          bounds=".quill-title-container"
                        />
                      </Box>
                    )}
                    </Box>

                    {/* Content Input */}
                    <Box sx={{ flex: 1, minHeight: '250px' }}>
                      <Typography
                        variant="subtitle1"
                        className="section-title"
                        style={{ fontWeight: 500, color: 'black' }}
                        sx={{ mb: 1 }}
                      >
                        Slide Content
                      </Typography>
                      {memoizedSlides.length > 0 && (
                        <Box sx={{ 
                          height: 'calc(100% - 30px)',
                          '& .ql-toolbar': {
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px',
                            borderColor: 'rgba(0, 0, 0, 0.1)',
                            borderBottom: 'none'
                          },
                          '& .ql-container': {
                            borderBottomLeftRadius: '8px',
                            borderBottomRightRadius: '8px',
                            borderColor: 'rgba(0, 0, 0, 0.1)',
                            height: 'calc(100% - 42px)'
                          },
                          '& .ql-editor': {
                            height: '100%',
                            minHeight: '180px',
                            padding: '12px 15px'
                          }
                        }}>
                          <ReactQuill
                            ref={editorRef}
                            key={`slide-editor-${currentSlideIndex}`}
                            theme="snow"
                            value={memoizedSlides[currentSlideIndex]?.content || ''}
                            onChange={handleContentChange}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Enter your slide content here..."
                            preserveWhitespace={true}
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Slide Navigation */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', my: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Slide {currentSlideIndex + 1} of {totalSlides}
                      </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                          <IconButton
                            size="small"
                            onClick={goToPrevSlide}
                            disabled={currentSlideIndex === 0}
                            sx={{
                              bgcolor: 'rgba(72, 144, 254, 0.1)',
                              '&:hover': {
                                bgcolor: 'rgba(72, 144, 254, 0.2)'
                              },
                              '&.Mui-disabled': {
                                bgcolor: 'rgba(0, 0, 0, 0.05)'
                              }
                            }}
                          >
                            <ArrowBackIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={goToNextSlide}
                            disabled={currentSlideIndex >= totalSlides - 1}
                            sx={{
                              bgcolor: 'rgba(72, 144, 254, 0.1)',
                              '&:hover': {
                                bgcolor: 'rgba(72, 144, 254, 0.2)'
                              },
                              '&.Mui-disabled': {
                                bgcolor: 'rgba(0, 0, 0, 0.05)'
                              }
                            }}
                          >
                            <ArrowForwardIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={handleAddSlide}
                          size="small"
                          sx={{
                            textTransform: 'none',
                            borderColor: 'var(--primary-color)',
                            color: 'var(--primary-color)',
                            borderRadius: '8px',
                            '&:hover': {
                              borderColor: 'var(--primary-dark)',
                              backgroundColor: 'rgba(72, 144, 254, 0.05)'
                            }
                          }}
                        >
                          Add Slide
                        </Button>
                        
                        <Button
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          onClick={handleDeleteSlide}
                          disabled={totalSlides <= 1}
                          size="small"
                          sx={{
                            textTransform: 'none',
                            borderColor: '#f44336',
                            color: '#f44336',
                            borderRadius: '8px',
                            '&:hover': {
                              borderColor: '#d32f2f',
                              backgroundColor: 'rgba(244, 67, 54, 0.05)'
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                    
                    {/* Clear All Content button */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                      <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={handleClearAllContent}
                        size="small"
                        sx={{
                          textTransform: 'none',
                          borderColor: '#f44336',
                          color: '#f44336',
                          borderRadius: '8px',
                          '&:hover': {
                            borderColor: '#d32f2f',
                            backgroundColor: 'rgba(244, 67, 54, 0.05)'
                          }
                        }}
                      >
                        Clear All Content
                      </Button>
                    </Box>
                  </Box>
                )}
                
                {/* Enhanced Customize Tab */}
                {activeTab === 'customize' && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1,
                    pb: 2 // Add bottom padding for better scrolling experience
                  }}>
                    {/* Profile Section */}
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ color: 'var(--primary-color)' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Profile
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Display Name */}
                          <TextField
                            fullWidth
                            label="Display Name"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            size="small"
                            variant="outlined"
                          />
                          
                          {/* Profile Image */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={profileImage || linkedinProfileData?.profile_picture || userProfile?.image}
                              sx={{ width: 50, height: 50 }}
                            >
                              {displayName?.[0]?.toUpperCase() || 'U'}
                            </Avatar>
                            <Button
                              variant="outlined"
                              component="label"
                              startIcon={<ImageIcon />}
                              size="small"
                            >
                              Change
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    const reader = new FileReader();
                                    reader.onloadend = () => setProfileImage(reader.result);
                                    reader.readAsDataURL(file);
                                  }
                                }}
                              />
                            </Button>
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* Colors Section */}
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PaletteIcon sx={{ color: 'var(--primary-color)' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Colors & Theme
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Custom Colors Toggle */}
                          <FormControlLabel
                            control={
                              <Switch
                                checked={useCustomColors}
                                onChange={(e) => setUseCustomColors(e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Use Custom Colors"
                          />
                          
                          {useCustomColors ? (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>Primary Color</Typography>
                                <TextField
                                  type="color"
                                  value={customColor1}
                                  onChange={(e) => setCustomColor1(e.target.value)}
                                  size="small"
                                  fullWidth
                                />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>Secondary Color</Typography>
                                <TextField
                                  type="color"
                                  value={customColor2}
                                  onChange={(e) => setCustomColor2(e.target.value)}
                                  size="small"
                                  fullWidth
                                />
                              </Box>
                            </Box>
                          ) : (
                            <FormControl fullWidth size="small">
                              <InputLabel>Theme</InputLabel>
                              <Select
                                value={theme}
                                onChange={handleThemeChange}
                                label="Theme"
                              >
                                {slideThemes.map(themeOption => (
                                  <MenuItem key={themeOption.value} value={themeOption.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          bgcolor: themeOption.primary,
                                          borderRadius: '50%'
                                        }}
                                      />
                                      <Box
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          bgcolor: themeOption.secondary,
                                          borderRadius: '50%',
                                          border: '1px solid rgba(0,0,0,0.1)'
                                        }}
                                      />
                                      {themeOption.label}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* Typography Section */}
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FormatSizeIcon sx={{ color: 'var(--primary-color)' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Typography
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Title Font Settings */}
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            Title Settings
                          </Typography>
                          
                          <FormControl fullWidth size="small">
                            <InputLabel>Title Font</InputLabel>
                            <Select
                              value={titleFont}
                              onChange={handleTitleFontChange}
                              label="Title Font"
                            >
                              {fontOptions.map(font => (
                                <MenuItem key={font.value} value={font.value} sx={{ fontFamily: font.family }}>
                                  {font.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ mb: 1 }}>Font Size: {titleFontSize}px</Typography>
                              <Slider
                                value={titleFontSize}
                                onChange={(e, value) => setTitleFontSize(value)}
                                min={20}
                                max={80}
                                size="small"
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ mb: 1 }}>Font Weight: {titleFontWeight}</Typography>
                              <Slider
                                value={titleFontWeight}
                                onChange={(e, value) => setTitleFontWeight(value)}
                                min={300}
                                max={900}
                                step={100}
                                size="small"
                              />
                            </Box>
                          </Box>

                          <Divider sx={{ my: 1 }} />

                          {/* Content Font Settings */}
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            Content Settings
                          </Typography>
                          
                          <FormControl fullWidth size="small">
                            <InputLabel>Content Font</InputLabel>
                            <Select
                              value={contentFont}
                              onChange={handleContentFontChange}
                              label="Content Font"
                            >
                              {fontOptions.map(font => (
                                <MenuItem key={font.value} value={font.value} sx={{ fontFamily: font.family }}>
                                  {font.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ mb: 1 }}>Font Size: {contentFontSize}px</Typography>
                              <Slider
                                value={contentFontSize}
                                onChange={(e, value) => setContentFontSize(value)}
                                min={12}
                                max={32}
                                size="small"
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ mb: 1 }}>Font Weight: {contentFontWeight}</Typography>
                              <Slider
                                value={contentFontWeight}
                                onChange={(e, value) => setContentFontWeight(value)}
                                min={300}
                                max={700}
                                step={100}
                                size="small"
                              />
                            </Box>
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ mb: 1 }}>Line Height: {lineHeight}</Typography>
                              <Slider
                                value={lineHeight}
                                onChange={(e, value) => setLineHeight(value)}
                                min={1}
                                max={2.5}
                                step={0.1}
                                size="small"
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ mb: 1 }}>Letter Spacing: {letterSpacing}px</Typography>
                              <Slider
                                value={letterSpacing}
                                onChange={(e, value) => setLetterSpacing(value)}
                                min={-2}
                                max={5}
                                step={0.5}
                                size="small"
                              />
                            </Box>
                          </Box>

                          {/* Text Alignment */}
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>Text Alignment</Typography>
                            <ButtonGroup size="small" fullWidth>
                              {['left', 'center', 'right'].map((align) => (
                                <Button
                                  key={align}
                                  variant={textAlign === align ? 'contained' : 'outlined'}
                                  onClick={() => setTextAlign(align)}
                                  sx={{ textTransform: 'capitalize' }}
                                >
                                  {align}
                                </Button>
                              ))}
                            </ButtonGroup>
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* Images Section */}
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhotoLibraryIcon sx={{ color: 'var(--primary-color)' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Images
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Image Upload */}
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button
                              variant="outlined"
                              component="label"
                              startIcon={<UploadIcon />}
                              size="small"
                            >
                              Upload Image
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleImageUpload}
                              />
                            </Button>
                            
                            {currentSlideImage && (
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<CloseIcon />}
                                onClick={handleRemoveImage}
                                size="small"
                              >
                                Remove
                              </Button>
                            )}
                          </Box>

                          {/* Image Preview */}
                          {currentSlideImage && (
                            <Box
                              component="img"
                              src={currentSlideImage}
                              sx={{
                                width: '100%',
                                maxHeight: 100,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '1px solid rgba(0,0,0,0.1)'
                              }}
                            />
                          )}

                          {/* Image Controls */}
                          {currentSlideImage && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {/* Position Selection */}
                              <Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>Position</Typography>
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={imagePosition}
                                    onChange={(e) => setImagePosition(e.target.value)}
                                  >
                                    <MenuItem value="top-left">Top Left</MenuItem>
                                    <MenuItem value="top-right">Top Right</MenuItem>
                                    <MenuItem value="bottom-left">Bottom Left</MenuItem>
                                    <MenuItem value="bottom-right">Bottom Right</MenuItem>
                                    <MenuItem value="center">Center</MenuItem>
                                    <MenuItem value="background">Background</MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>

                              {/* Size and Opacity */}
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ mb: 1 }}>Size: {imageSize}px</Typography>
                                  <Slider
                                    value={imageSize}
                                    onChange={(e, value) => setImageSize(value)}
                                    min={50}
                                    max={300}
                                    size="small"
                                  />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ mb: 1 }}>Opacity: {Math.round(imageOpacity * 100)}%</Typography>
                                  <Slider
                                    value={imageOpacity}
                                    onChange={(e, value) => setImageOpacity(value)}
                                    min={0.1}
                                    max={1}
                                    step={0.1}
                                    size="small"
                                  />
                                </Box>
                              </Box>

                              {/* Rotation */}
                              <Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>Rotation: {imageRotation}°</Typography>
                                <Slider
                                  value={imageRotation}
                                  onChange={(e, value) => setImageRotation(value)}
                                  min={-45}
                                  max={45}
                                  size="small"
                                />
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* Background & Effects Section */}
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ColorLensIcon sx={{ color: 'var(--primary-color)' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Background & Effects
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Background Style */}
                          <FormControl fullWidth size="small">
                            <InputLabel>Background Style</InputLabel>
                            <Select
                              value={backgroundStyle}
                              onChange={handleBackgroundStyleChange}
                              label="Background Style"
                            >
                              {backgroundStyles.map(style => (
                                <MenuItem key={style.value} value={style.value}>
                                  {style.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {/* Gradient Direction */}
                          {(backgroundStyle === 'gradient-linear' || backgroundStyle === 'gradient-diagonal') && (
                            <Box>
                              <Typography variant="body2" sx={{ mb: 1 }}>Gradient Direction: {gradientDirection}°</Typography>
                              <Slider
                                value={gradientDirection}
                                onChange={(e, value) => setGradientDirection(value)}
                                min={0}
                                max={360}
                                size="small"
                              />
                            </Box>
                          )}

                          {/* Border Settings */}
                          <FormControl fullWidth size="small">
                            <InputLabel>Border Style</InputLabel>
                            <Select
                              value={borderStyle}
                              onChange={handleBorderStyleChange}
                              label="Border Style"
                            >
                              {borderStyles.map(border => (
                                <MenuItem key={border.value} value={border.value}>
                                  {border.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {(borderStyle === 'solid' || borderStyle === 'dashed' || borderStyle === 'gradient') && (
                            <Box>
                              <Typography variant="body2" sx={{ mb: 1 }}>Border Width: {borderWidth}px</Typography>
                              <Slider
                                value={borderWidth}
                                onChange={(e, value) => setBorderWidth(value)}
                                min={1}
                                max={10}
                                size="small"
                              />
                            </Box>
                          )}

                          {/* Shadow Intensity */}
                          {(borderStyle === 'shadow' || borderStyle === 'glow') && (
                            <Box>
                              <Typography variant="body2" sx={{ mb: 1 }}>Shadow Intensity: {shadowIntensity}px</Typography>
                              <Slider
                                value={shadowIntensity}
                                onChange={(e, value) => setShadowIntensity(value)}
                                min={5}
                                max={50}
                                size="small"
                              />
                            </Box>
                          )}

                          {/* Visual Elements */}
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>Visual Elements</Typography>
                            <Grid container spacing={1}>
                              {visualElements.map(element => (
                                <Grid item xs={6} key={element.value}>
                                  <Chip
                                    label={element.label}
                                    variant={visualElement === element.value ? 'filled' : 'outlined'}
                                    color={visualElement === element.value ? 'primary' : 'default'}
                                    onClick={() => setVisualElement(element.value)}
                                    size="small"
                                    sx={{ width: '100%', justifyContent: 'flex-start' }}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* Layout Section */}
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CropFreeIcon sx={{ color: 'var(--primary-color)' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Layout
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Padding */}
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>Padding: {padding}px</Typography>
                            <Slider
                              value={padding}
                              onChange={(e, value) => setPadding(value)}
                              min={16}
                              max={80}
                              size="small"
                            />
                          </Box>

                          {/* Corner Radius */}
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>Corner Radius: {cornerRadius}px</Typography>
                            <Slider
                              value={cornerRadius}
                              onChange={(e, value) => setCornerRadius(value)}
                              min={0}
                              max={30}
                              size="small"
                            />
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
          
          {/* Right Panel - Preview */}
          <Grid item xs={12} md={7} lg={8} className="content-panel">
            <Paper 
              elevation={0} 
              className="content-paper" // Inherits basic structure
              sx={{ 
                borderRadius: 2, 
                // Override overflow to make Paper non-scrolling
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                // height: '100%', // Let flex manage height
                // minHeight: 'calc(100vh - 100px)', // Removed
              }}
            >
              {/* Preview Header - Sticky Part */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                pb: 2,
                mb: isModalMode ? 2 : 3, // Adjust margin for modal
                flexShrink: 0 // Make Header sticky
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" sx={{ color: '#2D3748' }}>
                    Preview
                  </Typography>
                  {!generating && slides.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Slide {currentSlideIndex + 1} of {totalSlides}
                  </Typography>
                  )}
                </Box>

                <Box sx={{ 
                  display: 'flex',
                  gap: 1,
                  mt: 0,
                  justifyContent: 'flex-end'
                }}>
                  <Tooltip title="Download PDF">
                    <span>
                      <IconButton
                        onClick={handleDownloadPDF} // This remains for non-modal regular download
                        disabled={downloading || slides.length === 0}
                        sx={{ color: '#4890fe' }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {isModalMode ? (
                    <Tooltip title="Use This Carousel">
                      <span>
                        <IconButton
                          onClick={handleUseThisCarousel} // New handler for modal
                          disabled={downloading || slides.length === 0} // Use downloading state
                          sx={{ color: '#28a745' }}
                        >
                          {downloading ? <CircularProgress size={20} color="inherit"/> : <PostAddIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Attach to Post">
                      <span>
                        <IconButton
                          onClick={() => {
                            fetchPosts();
                            setPostsDialogOpen(true);
                          }}
                          disabled={saving || slides.length === 0}
                          sx={{ color: '#28a745' }}
                        >
                          <PostAddIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {/* Preview Area - Now Scrollable */}
              <Box
                ref={previewRef}
                sx={{
                  flex: 1, // Takes up remaining space
                  display: 'flex',
                  flexDirection: 'column',
                  // overflow: 'hidden', // Changed to auto
                  overflow: 'auto', // This Box now scrolls its content
                  pr: 2, // Padding for scrollbar
                  minHeight: 0 // Crucial for flex calculation
                }}
              >
                {/* Main Preview Box (Content Box) */}
                <Box sx={{ 
                  flex: 1, // Allow this to grow if needed, but parent scrolls
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                  p: 0,
                  overflow: 'hidden', // Content Box itself should not scroll, just display
                  // height: '100%', // Removed
                  bgcolor: '#ffffff',
                  position: 'relative'
                }}>
                  {/* Left Arrow */}
                  <IconButton
                    onClick={goToPrevSlide}
                    disabled={currentSlideIndex === 0 || generating}
                    sx={{
                      position: 'absolute',
                      left: '15%',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.4,
                        bgcolor: 'rgba(255, 255, 255, 0.5)',
                      }
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  
                  {/* Right Arrow */}
                  <IconButton
                    onClick={goToNextSlide}
                    disabled={currentSlideIndex >= slides.length - 1 || generating}
                    sx={{
                      position: 'absolute',
                      right: '15%',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.4,
                        bgcolor: 'rgba(255, 255, 255, 0.5)',
                      }
                    }}
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                  
                  {generating ? (
                    <Box sx={{ 
                      width: '100%', 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <LoadingScreen message="Generating your carousel content..." />
                    </Box>
                  ) : memoizedSlides.length > 0 ? (
                    <Box 
                      sx={{ 
                        width: '100%',
                        maxWidth: slideSize === 'portrait' ? '400px' : '500px',
                        height: 'auto',
                        aspectRatio: slideSize === 'portrait' ? '4 / 5' : '1 / 1',
                        transition: 'all 0.3s ease-out',
                        opacity: 1,
                        transform: 'translateX(0)',
                        willChange: 'transform, opacity',
                        backfaceVisibility: 'hidden',
                        WebkitFontSmoothing: 'antialiased'
                      }}
                      className="carousel-slide-preview-container"
                    >
                      <CarouselSlidePreview
                        slideContent={memoizedSlides[currentSlideIndex]?.content || ''}
                        slideTitle={memoizedSlides[currentSlideIndex]?.title || ''}
                        slideIndex={currentSlideIndex}
                        totalSlides={memoizedSlides.length}
                        theme={theme}
                        fontPairing={fontPairing}
                        titleFont={titleFont}
                        contentFont={contentFont}
                        backgroundStyle={backgroundStyle}
                        visualElement={visualElement}
                        borderStyle={borderStyle}
                        userName={displayName || 'Your Name'}
                        userImage={profileImage}
                        previewRef={previewRef}
                        slideSize={slideSize}
                        // Enhanced customization props
                        titleFontSize={titleFontSize}
                        contentFontSize={contentFontSize}
                        titleFontWeight={titleFontWeight}
                        contentFontWeight={contentFontWeight}
                        lineHeight={lineHeight}
                        letterSpacing={letterSpacing}
                        textAlign={textAlign}
                        padding={padding}
                        cornerRadius={cornerRadius}
                        slideImage={currentSlideImage}
                        imagePosition={imagePosition}
                        imageSize={imageSize}
                        imageOpacity={imageOpacity}
                        imageRotation={imageRotation}
                        shadowIntensity={shadowIntensity}
                        gradientDirection={gradientDirection}
                        borderWidth={borderWidth}
                        customColor1={customColor1}
                        customColor2={customColor2}
                        useCustomColors={useCustomColors}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Box 
                        sx={{ 
                          width: 120,
                          height: 120,
                          borderRadius: '50%',
                          bgcolor: 'rgba(72, 144, 254, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3
                        }}
                      >
                        <AddIcon sx={{ fontSize: 48, color: '#4890fe' }} />
                      </Box>
                      <Typography 
                        variant="h6"
                        sx={{ 
                          color: '#2D3748',
                          fontWeight: 600,
                          mb: 1
                        }}
                      >
                        Ready to create a carousel?
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ 
                          color: '#4A5568',
                          maxWidth: 400,
                          textAlign: 'center'
                        }}
                      >
                        Start by adding slides and customizing their content<br />
                        using the editor on the left
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Success Dialog */}
        <Dialog
          open={openSuccessDialog}
          onClose={() => setOpenSuccessDialog(false)}
          maxWidth="xs"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 2,
              padding: 2
            }
          }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 3 }}>
            <Typography 
              variant="h6" 
              className="component-heading"
              sx={{ 
                mb: 2
              }}
            >
              Carousel Saved Successfully!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your carousel has been saved. You can view it in My Posts.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => setOpenSuccessDialog(false)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setOpenSuccessDialog(false);
                  navigate('/dashboard/posts');
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: 'var(--primary-color)',
                  '&:hover': {
                    bgcolor: 'var(--primary-dark)'
                  }
                }}
              >
                View Posts
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>

        {/* Add Posts Dialog - Conditionally render if not in modal mode */}
        {!isModalMode && postsDialogOpen && (
          <Dialog 
            open={postsDialogOpen} 
            onClose={() => setPostsDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            sx={{
              '& .MuiDialog-paper': {
                borderRadius: 2,
                overflow: 'hidden'
              }
            }}
          >
            <DialogTitle sx={{ 
              bgcolor: '#f8fafc',
              borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
              fontWeight: 600
            }}>
              Select Post
            </DialogTitle>
            <DialogContent>
              <List>
                {availablePosts.map((post) => (
                  <ListItem key={post.id} disablePadding>
                    <ListItemButton onClick={() => handleAttachToPost(post.id)}>
                      <ListItemText 
                        primary={post.topic || 'Untitled Post'} 
                        secondary={post.content}
                        secondaryTypographyProps={{
                          noWrap: true,
                          style: { maxWidth: '100%' }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                {availablePosts.length === 0 && (
                  <ListItem>
                    <ListItemText 
                      primary="No posts available" 
                      secondary="Create a post first to attach carousel"
                    />
                  </ListItem>
                )}
              </List>
            </DialogContent>
          </Dialog>
        )}

        {/* Success Message Alert - Conditionally render if not in modal mode */}
        {!isModalMode && successMessage && (
          <Alert 
            severity="success" 
            sx={{ 
              position: 'fixed', 
              bottom: 16, 
              right: 16, 
              zIndex: 9999,
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}
            onClose={() => setSuccessMessage('')}
          >
            {successMessage}
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default GenerateCarousel; 